import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ShieldCheck, Save, X } from 'lucide-react';
import adminApi from '../api/adminApi';
import { useOnce } from '../hooks/useOnce';

// Settings -> Security -> Two-Factor Authentication
const SecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saved, setSaved] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(5);
  const [expiryError, setExpiryError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!showConfirm) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowConfirm(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showConfirm]);

  const applyData = (data) => {
    setSaved(data);
    setEnabled(data.enabled);
    setExpiryMinutes(data.otp_expiry_minutes);
  };

  const fetchSettings = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await adminApi.getTwoFactorSettings();
      applyData(response.data.data);
    } catch (error) {
      setLoadError(error.response?.data?.message || 'Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  useOnce(() => {
    fetchSettings();
  }, []);

  const validateExpiry = (value) => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 1 || n > 30) return 'Enter a whole number of minutes between 1 and 30';
    return '';
  };

  const dirty = saved && (enabled !== saved.enabled || Number(expiryMinutes) !== saved.otp_expiry_minutes);

  const handleSave = async (e) => {
    e.preventDefault();
    const err = validateExpiry(expiryMinutes);
    setExpiryError(err);
    if (err) return;

    // Turning 2FA on affects every user's next sign-in - confirm first.
    if (enabled && !saved.enabled) {
      setShowConfirm(true);
      return;
    }
    await save();
  };

  const save = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      const response = await adminApi.updateTwoFactorSettings({ enabled, otp_expiry_minutes: Number(expiryMinutes) });
      applyData(response.data.data);
      toast.success(response.data.message || 'Security settings saved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    border: '1px solid #e5e7eb',
    padding: '2rem',
    maxWidth: '640px'
  };

  if (loading) {
    return <div style={cardStyle}><p style={{ color: '#6b7280' }}>Loading security settings...</p></div>;
  }

  if (loadError) {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{loadError}</p>
        <button onClick={fetchSettings} style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <form onSubmit={handleSave}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ShieldCheck size={22} color="#1d4ed8" />
            </div>
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: '0 0 0.25rem' }}>Two-Factor Authentication</h3>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                After a correct username and password, users must also enter a 6-digit code emailed to their registered address.
              </p>
            </div>
          </div>
          <span style={{
            padding: '0.25rem 0.625rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            backgroundColor: saved.enabled ? '#dcfce7' : '#f3f4f6',
            color: saved.enabled ? '#15803d' : '#6b7280'
          }}>
            2FA Status: {saved.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
          <label htmlFor="twofa-toggle" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
            Enable 2FA
          </label>
          <button
            id="twofa-toggle"
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => setEnabled(!enabled)}
            style={{
              position: 'relative',
              width: '44px',
              height: '24px',
              borderRadius: '9999px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: enabled ? '#3b82f6' : '#d1d5db',
              transition: 'background-color 0.15s ease',
              flexShrink: 0
            }}
          >
            <span style={{
              position: 'absolute',
              top: '2px',
              left: enabled ? '22px' : '2px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: 'white',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              transition: 'left 0.15s ease'
            }} />
            <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
              {enabled ? 'On' : 'Off'}
            </span>
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="otp-expiry" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
            OTP expiry (minutes)
          </label>
          <input
            id="otp-expiry"
            type="number"
            min="1"
            max="30"
            value={expiryMinutes}
            onChange={(e) => {
              setExpiryMinutes(e.target.value);
              setExpiryError(validateExpiry(e.target.value));
            }}
            style={{
              width: '160px',
              padding: '0.625rem 0.75rem',
              border: `1px solid ${expiryError ? '#dc2626' : '#d1d5db'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}
          />
          {expiryError && <p style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#dc2626' }}>{expiryError}</p>}
          <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#6b7280' }}>
            Codes can be used once. After {saved.otp_max_attempts} wrong entries the code is invalidated,
            and a new code can be requested every {saved.otp_resend_interval_seconds} seconds.
          </p>
        </div>

        {saved.updated_at && (
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1.5rem' }}>
            Last updated: {new Date(saved.updated_at.replace(' ', 'T')).toLocaleString()}
            {saved.updated_by ? ` by ${saved.updated_by}` : ''}
          </p>
        )}

        <button
          type="submit"
          disabled={saving || !dirty}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.75rem 1.5rem',
            backgroundColor: saving || !dirty ? '#93c5fd' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: saving || !dirty ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          <Save size={18} style={{ marginRight: '0.5rem' }} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </form>

      {showConfirm && (
        // zIndex above Sidebar's 1000/1001, which would otherwise cover part of the modal.
        <div
          onClick={() => setShowConfirm(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="twofa-confirm-title"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '0.75rem', width: '100%', maxWidth: '440px', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.25)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldCheck size={18} color="#1d4ed8" />
                </div>
                <h3 id="twofa-confirm-title" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>
                  Enable two-factor authentication?
                </h3>
              </div>
              <button onClick={() => setShowConfirm(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.6 }}>
              From the next sign-in, every user will need a 6-digit code emailed to their registered address.
              Make sure all users have a valid email.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                style={{ padding: '0.625rem 1.25rem', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                type="button"
                autoFocus
                onClick={save}
                style={{ padding: '0.625rem 1.5rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
